import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { VIBRANT_THEMES, vibrantTheme } from "./common";
import api from "../lib/api";

/* Renderer for long-form section guides. Guides are stored as raw text
   (verbatim user copy) with "## " prefixed headings; parseGuide() converts
   them into ordered sections/blocks. Chips auto-link to matching article
   pages from the TravelerPulse library as they are published. */

const slugifyTerm = (s) => s.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

let _slugCache = null;
let _slugPromise = null;
const loadArticleSlugs = () => {
  if (_slugCache) return Promise.resolve(_slugCache);
  if (!_slugPromise) {
    _slugPromise = api.get("/articles-imported").then((r) => {
      _slugCache = new Set((r.data.items || []).map((i) => i.slug));
      return _slugCache;
    }).catch(() => new Set());
  }
  return _slugPromise;
};

export const matchArticleSlug = (term, slugs) => {
  if (!slugs || !slugs.size) return null;
  const s = slugifyTerm(term);
  if (!s) return null;
  const candidates = [s, `${s}-explained`, s.endsWith("s") ? s.slice(0, -1) : `${s}s`,
    `how-${s}-works`, `${s}-travel-guide`];
  for (const c of candidates) if (slugs.has(c)) return c;
  return null;
};

export const useArticleSlugs = () => {
  const [slugs, setSlugs] = useState(_slugCache);
  useEffect(() => { loadArticleSlugs().then(setSlugs); }, []);
  return slugs;
};

const EMOJI_RE = /^(\p{Extended_Pictographic}(?:\uFE0F)?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F)?)*)\s*/u;

export function parseGuide(raw) {
  const sections = [];
  let cur = { e: null, t: null, blocks: [] };
  let chips = [];
  const flushChips = () => {
    if (!chips.length) return;
    if (chips.length === 1) cur.blocks.push({ type: "label", text: chips[0] });
    else cur.blocks.push({ type: "chips", items: chips });
    chips = [];
  };
  for (const rawLine of raw.split("\n")) {
    const line = rawLine.trim();
    if (!line) { flushChips(); continue; }
    if (line.startsWith("## ")) {
      flushChips();
      if (cur.blocks.length) sections.push(cur);
      let t = line.slice(3).trim();
      let e = null;
      const m = t.match(EMOJI_RE);
      if (m) { e = m[1]; t = t.slice(m[0].length); }
      cur = { e, t, blocks: [] };
      continue;
    }
    if (line.includes("\u2192")) {
      flushChips();
      cur.blocks.push({ type: "flow", items: line.split("\u2192").map((s) => s.trim().replace(/^\u2192\s*/, "")).filter(Boolean) });
      continue;
    }
    if (line.length > 64 || /[.:?!]$/.test(line)) {
      flushChips();
      cur.blocks.push({ type: "p", text: line });
      continue;
    }
    chips.push(line);
  }
  flushChips();
  if (cur.blocks.length) sections.push(cur);
  return sections;
}

export const Chips = ({ items, theme }) => {
  const slugs = useArticleSlugs();
  return (
    <div className="flex flex-wrap gap-2 my-4">
      {items.map((c, i) => {
        const slug = matchArticleSlug(c, slugs);
        return slug ? (
          <RouterLink key={`${c}-${i}`} to={`/articles/${slug}`} data-testid={`chip-link-${slug}`}
            className="px-3 py-1.5 text-sm border font-600 underline decoration-dotted underline-offset-2 hover:decoration-solid transition-colors"
            style={{ borderColor: theme.bg, color: theme.bg, backgroundColor: `${theme.bg}1A` }}>
            {c}
          </RouterLink>
        ) : (
          <span key={`${c}-${i}`} className="px-3 py-1.5 text-sm border font-600"
            style={{ borderColor: theme.bg, color: theme.bg, backgroundColor: `${theme.bg}0D` }}>
            {c}
          </span>
        );
      })}
    </div>
  );
};

export const Flow = ({ items, theme }) => (
  <div className="flex flex-wrap items-center gap-y-2 my-4">
    {items.map((step, i) => (
      <span key={`${step}-${i}`} className="flex items-center">
        <span className="px-2.5 py-1 text-sm font-700 text-white" style={{ backgroundColor: theme.bg }}>{step}</span>
        {i < items.length - 1 && <span className="mx-1.5 font-mono text-sm" style={{ color: theme.bg }}>{"\u2192"}</span>}
      </span>
    ))}
  </div>
);

const Block = ({ b, theme }) => {
  const slugs = useArticleSlugs();
  if (b.type === "p") return <p>{b.text}</p>;
  if (b.type === "label") {
    const slug = matchArticleSlug(b.text, slugs);
    return slug
      ? <RouterLink to={`/articles/${slug}`} className="block font-heading font-700 text-lg pt-1 underline decoration-dotted underline-offset-4 hover:decoration-solid" style={{ color: theme.bg }}>{b.text}</RouterLink>
      : <h3 className="font-heading font-700 text-lg pt-1" style={{ color: theme.bg }}>{b.text}</h3>;
  }
  if (b.type === "chips") return <Chips items={b.items} theme={theme} />;
  if (b.type === "flow") return <Flow items={b.items} theme={theme} />;
  return null;
};

export const ParsedSections = ({ sections, prefix = "guide" }) => (
  <>
    {sections.map((s, idx) => {
      const theme = VIBRANT_THEMES[idx % VIBRANT_THEMES.length];
      const slug = (s.t || `intro-${idx}`).toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
      return (
        <section key={`${slug}-${idx}`} data-testid={`${prefix}-section-${slug}`}>
          {s.t && (
            <div className="flex items-center gap-3 mb-4">
              {s.e && <span className="w-10 h-10 flex items-center justify-center text-lg text-white shrink-0" style={{ backgroundColor: theme.bg }} aria-hidden="true">{s.e}</span>}
              <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight">{s.t}</h2>
            </div>
          )}
          <div className="space-y-3 text-zinc-700 leading-relaxed text-base">
            {s.blocks.map((b, i) => <Block key={i} b={b} theme={theme} />)}
          </div>
        </section>
      );
    })}
  </>
);

// Short excerpt card shown on a section page, linking to the full guide article.
export const GuideTeaser = ({ guide }) => {
  if (!guide) return null;
  const theme = vibrantTheme(`guide-${guide.mode}`);
  return (
    <div className="border border-zinc-300 bg-white p-5 sm:p-6 mb-8 border-l-4" style={{ borderLeftColor: theme.bg }}
      data-testid={`${guide.mode}-guide-teaser`}>
      <div className="flex items-center gap-3 mb-2">
        <span className="w-9 h-9 flex items-center justify-center text-base text-white shrink-0" style={{ backgroundColor: theme.bg }} aria-hidden="true">{guide.emoji}</span>
        <div>
          <div className="font-heading font-900 text-lg leading-tight">{guide.title}</div>
          <div className="text-xs font-mono uppercase tracking-widest" style={{ color: theme.bg }}>{guide.subtitle}</div>
        </div>
      </div>
      <p className="text-sm text-zinc-700 leading-relaxed line-clamp-3">{guide.teaser[0]}</p>
      <RouterLink to={`/guides/${guide.mode}`} data-testid={`${guide.mode}-guide-link`}
        className="inline-flex items-center gap-1 mt-3 text-sm font-700 hover:underline" style={{ color: theme.bg }}>
        Read the full {guide.linkLabel} guide {"\u2192"}
      </RouterLink>
    </div>
  );
};
